import { createSignal } from "solid-js";
import type { DiffWorker, DiffModel } from "@fastdiff/worker";
import { Lines } from "../lines";

export type DiffStatus = "empty" | "loading" | "ready" | "error";

export interface FileSourceData {
  bytes: Uint8Array;
  name: string;
}

export interface DiffSnapshot {
  model: DiffModel;
  leftLines: Lines;
  rightLines: Lines;
}

export interface DiffTiming {
  decodeMs: number;
  diffMs: number;
  buildRowsMs: number;
}

export interface DiffStore {
  status: () => DiffStatus;
  snapshot: () => DiffSnapshot | null;
  /** Convenience: same as snapshot()?.model. */
  model: () => DiffModel | null;
  error: () => string | null;
  load: (left: FileSourceData, right: FileSourceData) => Promise<void>;
  reset: () => void;
  timing: () => DiffTiming | null;
  setBuildRowsMs: (ms: number) => void;
}

export function createDiffStore(worker: DiffWorker): DiffStore {
  const [status, setStatus] = createSignal<DiffStatus>("empty");
  const [snapshot, setSnapshot] = createSignal<DiffSnapshot | null>(null);
  const [error, setError] = createSignal<string | null>(null);
  const [timing, setTiming] = createSignal<DiffTiming | null>(null);

  async function load(left: FileSourceData, right: FileSourceData): Promise<void> {
    setStatus("loading");
    setError(null);
    setSnapshot(null);
    setTiming(null);
    try {
      const decodeRes = await worker.detectAndDecode(left.bytes, right.bytes);
      const decoded = decodeRes.result;
      const decodeMs = decodeRes.timingMs;
      if (decoded.leftContainsNul || decoded.rightContainsNul) {
        setStatus("error");
        setError(
          decoded.leftContainsNul && decoded.rightContainsNul
            ? "両方のファイルがバイナリのようです（NUL バイトを含む）。バイナリ差分には対応していません。"
            : `${decoded.leftContainsNul ? left.name : right.name} はバイナリのようです（NUL バイトを含む）。バイナリ差分には対応していません。`,
        );
        return;
      }
      const diffRes = await worker.diff(decoded.leftText, decoded.rightText, {
        meta: {
          leftName: left.name,
          rightName: right.name,
          leftBytes: left.bytes.length,
          rightBytes: right.bytes.length,
        },
      });
      const result = diffRes.result;
      const diffMs = diffRes.timingMs;
      // worker.diff always reports UTF-8 encoding (the diff is over already-
      // decoded text); replace with the actually-detected encoding from the
      // decode step. Use a new object to avoid mutating WASM-returned data.
      const model = { ...result, encoding: decoded.encoding };
      setSnapshot({
        model,
        leftLines: Lines.fromString(decoded.leftText),
        rightLines: Lines.fromString(decoded.rightText),
      });
      setTiming({ decodeMs, diffMs, buildRowsMs: 0 });
      setStatus("ready");
    } catch (e) {
      setStatus("error");
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function reset(): void {
    setStatus("empty");
    setSnapshot(null);
    setError(null);
    setTiming(null);
  }

  function setBuildRowsMs(ms: number): void {
    setTiming((cur) => (cur === null ? null : { ...cur, buildRowsMs: ms }));
  }

  const model = () => snapshot()?.model ?? null;

  return { status, snapshot, model, error, load, reset, timing, setBuildRowsMs };
}
